// Formats what is typed into an input — a PSČ "811 01", a rodné číslo "150310 / 0005", an IBAN in groups of four —
// without throwing the caret to the end of the value, which assigning `input.value` does (audit UI-38).
//
// The value is made of significant characters (digits, the IBAN's letters) and separators the format inserts. The
// caret is kept after as many significant characters as it followed before, so typing or deleting in the middle
// stays in the middle. A Backspace that removed only a separator — which the format would put straight back — removes
// the significant character in front of it instead, as upstream's formatPsc does.

// The significant characters of `value`, in order.
function significantOf(value, pattern) {
  return [...value].filter((character) => pattern.test(character)).join("")
}

// The position right after the `count`-th significant character of `value` (0 for none).
function positionAfter(value, count, pattern) {
  let seen = 0
  let position = 0
  while (seen < count && position < value.length) {
    if (pattern.test(value[position])) seen += 1
    position += 1
  }
  return position
}

// Rewrites `input.value` as `format(significant)` and restores the caret.
// @param {HTMLInputElement} input
// @param {{ significant: RegExp, format: (significant: string) => string, previous?: string, inputType?: string }} options
//   `significant` tests one character; `previous` is the value before this edit, `inputType` the InputEvent's.
// @returns {string} the formatted value
export function reformatKeepingCaret(input, { significant, format, previous = "", inputType = "" }) {
  const value = input.value
  const caret = input.selectionStart ?? value.length
  let digits = significantOf(value, significant)
  let before = significantOf(value.slice(0, caret), significant).length

  // Backspace took a separator away and nothing else: take the character in front of it.
  if (inputType === "deleteContentBackward" && before > 0 && digits === significantOf(previous, significant)) {
    digits = digits.slice(0, before - 1) + digits.slice(before)
    before -= 1
  }

  const formatted = format(digits)
  input.value = formatted
  const kept = significantOf(formatted, significant).length
  const position = positionAfter(formatted, Math.min(before, kept), significant)
  // Only the focused field has a caret to restore; setting a selection elsewhere would move focus in some browsers.
  if (input.ownerDocument?.activeElement === input) input.setSelectionRange(position, position)
  return formatted
}

// The Slovak shape of an amount the field holds — "1 234,50" for "1234.5" —, or null when it is not one. The wizard
// takes either (TaxFormController#valid_amount?) and shows this one (FinanceManagement::TaxFormHelper#tax_form_amount_value).
export function slovakAmount(text) {
  const compact = String(text ?? "").replace(/\s/g, "")
  const match = compact.match(/^(\d+)(?:[.,](\d{1,2}))?$/)
  if (!match) return null

  const whole = match[1].replace(/^0+(?=\d)/, "")
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
  return `${grouped},${(match[2] ?? "").padEnd(2, "0")}`
}
